import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calendar, Filter, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";
import {
  PeriodValue,
  ComparisonPeriod,
  createDefaultComparison,
  formatDateToISO,
} from "@/features/filters/filter.types";
import {
  comparisonOptions,
  calculateDateRange,
  getPeriodLabel,
  getComparisonOption,
} from "@/features/filters/filter.utils";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { cn } from "@/lib/utils";

interface GlobalFilterProps {
  className?: string;
  rightSlot?: React.ReactNode;
}

const GlobalFilter: React.FC<GlobalFilterProps> = ({
  className = "",
  rightSlot,
}) => {
  const {
    filter,
    isInitialized,
    applyFilter,
    setPeriod,
    setComparison,
    setCustomRange,
    resetFilters,
  } = useGlobalFilter();

  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  // Sincronizar datas personalizadas com o filtro atual
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (filter.period === "custom" && filter.customDateRange) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCustomStartDate(formatDateToISO(filter.customDateRange.startDate));
      setCustomEndDate(formatDateToISO(filter.customDateRange.endDate));
    } else {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  }, [filter.period, filter.customDateRange, isInitialized]);

  const isCustomDateMode = filter.period === "custom";

  const periodOptions = useMemo(
    () => [
      { value: "today", label: "Hoje" },
      { value: "yesterday", label: "Ontem" },
      { value: "24h", label: "24 horas" },
      { value: "7d", label: "Últimos 7 dias" },
      { value: "14d", label: "14 dias" },
      { value: "30d", label: "30 dias" },
      { value: "custom", label: "Personalizado" },
    ],
    [],
  );

  const handlePeriodChange = useCallback(
    (value: PeriodValue) => {
      setPeriod(value);
      if (value !== "custom") {
        setCustomStartDate("");
        setCustomEndDate("");
      }
    },
    [setPeriod],
  );

  const handleComparisonChange = useCallback(
    (value: string) => {
      const comparison =
        value === "none"
          ? createDefaultComparison()
          : getComparisonOption(value as ComparisonPeriod["type"]);
      setComparison(comparison);
    },
    [setComparison],
  );

  const handleApplyCustomFilter = useCallback(() => {
    if (!customStartDate || !customEndDate) {
      return;
    }

    setCustomRange({ startDate: customStartDate, endDate: customEndDate });
  }, [customStartDate, customEndDate, setCustomRange]);

  const handleClearFilters = useCallback(() => {
    resetFilters();
    setCustomStartDate("");
    setCustomEndDate("");
  }, [resetFilters]);

  const formatPeriodDisplay = useCallback(
    (selectedPeriod: PeriodValue): string => {
      const range = calculateDateRange(
        selectedPeriod,
        customStartDate,
        customEndDate,
      );
      const startFormatted = format(range.startDate, "dd MMM", {
        locale: ptBR,
      });
      const endFormatted = format(range.endDate, "dd MMM", { locale: ptBR });

      if (selectedPeriod === "today") {
        return format(range.startDate, "dd MMM yyyy", { locale: ptBR });
      }

      return `${startFormatted} — ${endFormatted}`;
    },
    [customStartDate, customEndDate],
  );

  const getComparisonPeriodLabel = useCallback(
    (comparisonType?: ComparisonPeriod["type"]): string => {
      if (!comparisonType || comparisonType === "none") {
        return "";
      }

      const comparison = getComparisonOption(comparisonType);
      return comparison.label;
    },
    [],
  );

  return (
    <div
      className={cn(
        "w-full rounded-xl border border-border/60 bg-card p-3 shadow-soft",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Left side - Period info */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground whitespace-nowrap">
              Período:
            </span>
            <Badge
              variant="secondary"
              className="font-medium whitespace-nowrap"
            >
              {getPeriodLabel(filter.period)}
            </Badge>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatPeriodDisplay(filter.period)}
            </span>
          </div>

          {filter.comparison && filter.comparison.type !== "none" && (
            <div className="flex items-center gap-2 text-sm sm:border-l sm:border-border/60 sm:pl-3">
              <span className="text-muted-foreground">vs</span>
              <Badge
                variant="outline"
                className="font-medium whitespace-nowrap"
              >
                {getComparisonPeriodLabel(filter.comparison?.type)}
              </Badge>
            </div>
          )}
        </div>

        {/* Right side - Controls */}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {rightSlot && (
            <div className="flex items-center gap-2 sm:border-r sm:border-border/60 sm:pr-2">
              {rightSlot}
            </div>
          )}
          {/* Custom date inputs */}
          {isCustomDateMode && (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Input
                type="date"
                value={customStartDate}
                onChange={(event) => setCustomStartDate(event.target.value)}
                className="h-8 w-full sm:w-32 text-xs"
              />
              <span className="text-muted-foreground text-xs hidden sm:inline">
                —
              </span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(event) => setCustomEndDate(event.target.value)}
                className="h-8 w-full sm:w-32 text-xs"
              />
            </div>
          )}

          {/* Quick period buttons */}
          <div className="flex items-center gap-1 sm:border-l sm:border-border/60 sm:pl-2">
            {["7d", "14d", "30d"].map((value) => (
              <Button
                key={value}
                size="xs"
                variant={filter.period === value ? "default" : "ghost"}
                onClick={() => handlePeriodChange(value as PeriodValue)}
                className="text-xs font-medium"
              >
                {value}
              </Button>
            ))}
          </div>

          {/* Period selector */}
          <Select value={filter.period} onValueChange={handlePeriodChange}>
            <SelectTrigger className="h-8 w-full sm:w-32 text-xs">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="text-xs"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Comparison selector */}
          <Select
            value={filter.comparison?.type || "none"}
            onValueChange={handleComparisonChange}
          >
            <SelectTrigger className="h-8 w-full sm:w-36 text-xs">
              <SelectValue placeholder="Comparação" />
            </SelectTrigger>
            <SelectContent>
              {comparisonOptions.map((option) => (
                <SelectItem
                  key={option.type}
                  value={option.type}
                  className="text-xs"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {isCustomDateMode && (
              <Button
                size="xs"
                onClick={handleApplyCustomFilter}
                disabled={!customStartDate || !customEndDate}
                className="gap-1"
              >
                <Filter className="h-3 w-3" />
                Filtrar
              </Button>
            )}

            <Button
              size="xs"
              variant="ghost"
              onClick={handleClearFilters}
              className="gap-1 text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" />
              Limpar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalFilter;
